import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { useCurrentUser } from '@/features/account';
import {
  useGetDocumentSignersQuery,
  useInviteDirectorsToSignMutation,
} from '@/features/annual-review';
import { toastShown } from '@/features/ui';

import { signableDocumentIdOf, titleOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';

interface InviteSignersDialogProps {
  item: OutstandingItem;
  onClose: () => void;
}

const nameOf = (signer: { name1: string; name2: string; name3: string; familyName: string }) =>
  [signer.name1, signer.name2, signer.name3, signer.familyName]
    .filter((part) => part !== '')
    .join(' ');

/**
 * Invites co-directors to sign a document.
 *
 * The legacy flow called the signers endpoint from the menu item's click
 * handler, threw an error toast when nobody came back, and only then opened a
 * dialog — so the request was paid for on every menu open and the director got
 * an error instead of an explanation. Here the dialog opens immediately and
 * reports its own emptiness, which is one request fewer and a better answer.
 *
 * Directors who have already signed are listed but not selectable: seeing that
 * two of four are done is the information the director came for.
 */
export const InviteSignersDialog = ({ item, onClose }: InviteSignersDialogProps) => {
  const dispatch = useAppDispatch();
  const { memberId } = useCurrentUser();
  const documentId = signableDocumentIdOf(item);

  const {
    data: signers = [],
    isLoading,
    isError,
  } = useGetDocumentSignersQuery({ asicCompanyId: item.asicCompanyId, documentId });
  const [inviteDirectors] = useInviteDirectorsToSignMutation();

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const invitable = signers.filter(
    (signer) => !signer.hasSigned && signer.asicMember.id !== memberId,
  );
  const alreadySigned = signers.filter((signer) => signer.hasSigned);

  const chosen = invitable.filter((signer) => selected[signer.asicMember.id] === true);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setFormError('');

    try {
      await inviteDirectors({
        documentsId: documentId,
        invitedDirectors: chosen.map((signer) => ({
          companyMemberId: signer.asicMember.id,
          invitedByMemberId: memberId,
          email: emails[signer.asicMember.id]?.trim() ?? '',
        })),
      }).unwrap();
      dispatch(toastShown('Invitations sent.', 'success'));
      onClose();
    } catch {
      setFormError('Those invitations could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const missingEmail = chosen.some((signer) => {
    const email = emails[signer.asicMember.id]?.trim() ?? '';
    return email === '';
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Invite others to sign</DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {formError !== '' && (
          <Alert severity="error" role="alert" className="rounded-lg">
            {formError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          {titleOf(item)} — {item.asicCompanyName}
        </Typography>

        {isError && (
          <Alert severity="error" role="alert" className="rounded-lg">
            We couldn&apos;t load the signatories for this document.
          </Alert>
        )}

        {!isLoading && !isError && invitable.length === 0 && (
          <Alert severity="info" className="rounded-lg">
            {alreadySigned.length > 0
              ? 'Everyone else has already signed this document.'
              : 'There are no other signatories on this document.'}
          </Alert>
        )}

        {invitable.map((signer) => {
          const id = signer.asicMember.id;
          const isChecked = selected[id] === true;

          return (
            <div key={id} className="flex flex-col gap-2 rounded-lg border border-divider p-3">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(event) => {
                      setSelected((current) => ({ ...current, [id]: event.target.checked }));
                    }}
                  />
                }
                label={nameOf(signer.asicMember)}
              />

              {isChecked && (
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={emails[id] ?? ''}
                  onChange={(event) => {
                    setEmails((current) => ({ ...current, [id]: event.target.value }));
                  }}
                />
              )}
            </div>
          );
        })}

        {alreadySigned.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Already signed:{' '}
            {alreadySigned.map((signer) => nameOf(signer.asicMember)).join(', ')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className="flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end">
        <Button onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={chosen.length === 0 || missingEmail || isSubmitting}
          className="w-full sm:w-auto"
        >
          {isSubmitting ? 'Sending…' : 'Send invitations'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
